import { useMemo, useState } from 'react'

import {
    ClientPreviewFieldsFragment,
    useGetPatientNameQuery,
} from '~graphql/generated/graphql'
import { PatientIdAndName } from './types'
import { SetStateFn } from '~config/constants'
import { getAlivePatients } from '~utils/helpers'

type SelectedPatientType = {
    selectedPatient: PatientIdAndName | undefined
    patientId: string | undefined
    setPatientId: SetStateFn<string | undefined>
    resetPatientId: () => void
    showMore: boolean
    setShowMore: SetStateFn<boolean>
    needsShowMore: boolean
    patients: PatientIdAndName[]
}

export default function useSelectedPatient(
    client: ClientPreviewFieldsFragment,
    patientFilter: string[],
): SelectedPatientType {
    const sortedPatients = useMemo(
        () =>
            [...getAlivePatients(client.patients)].sort(
                (a, b) => a.name?.localeCompare(b.name || '') || 0,
            ),
        [client.patients],
    )

    const defaultPatientId = patientFilter.at(0) ?? sortedPatients.at(0)?.id

    const [showMore, setShowMore] = useState(false)
    const [patientId, setPatientId] = useState<string | undefined>(
        defaultPatientId,
    )

    const { data } = useGetPatientNameQuery({
        variables: {
            id: patientId || '',
        },
        skip: !patientId,
    })

    const selectedPatientFromList = useMemo(
        () => sortedPatients.find(patient => patient.id === patientId),
        [patientId, sortedPatients],
    )
    const selectedPatient = data?.patient ?? selectedPatientFromList
    const needsShowMore = sortedPatients.length > 15

    const activeIndex = sortedPatients.findIndex(
        patient => patient.id === selectedPatient?.id,
    )
    const patientsToDisplay =
        needsShowMore && !showMore
            ? [
                  ...sortedPatients.slice(0, activeIndex > 14 ? 14 : 15),
                  ...(activeIndex > 14 && selectedPatient
                      ? [selectedPatient]
                      : []),
              ]
            : sortedPatients

    return {
        selectedPatient,
        patientId,
        setPatientId,
        resetPatientId: () => {
            setPatientId(defaultPatientId)
            setShowMore(false)
        },
        showMore,
        setShowMore,
        needsShowMore,
        patients: patientsToDisplay,
    }
}
